// web/src/pages/BrandPage.jsx
import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import HeaderAuth from "../components/common/HeaderAuth";
import { Link } from "react-router-dom";

export default function BrandPage() {
  const origin =
    (typeof window !== "undefined" && window.location?.origin) ||
    "https://etatdulieu.com";

  const pageUrl = `${origin}/etat-du-lieu`;
  const mail = "contact.etatdulieu@gmail.com";

  useEffect(() => {
    try {
      document.documentElement.style.colorScheme = "light";
    } catch {}
  }, []);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "État du Lieu – outil de rapport d’adresse",
    url: pageUrl,
    isPartOf: { "@type": "WebSite", name: "État du Lieu", url: origin },
    description:
      "Page de présentation d’État du Lieu, outil de rapport d’adresse, à ne pas confondre avec l’état des lieux locatif.",
    dateModified: "2025-11-25",
  };

  return (
    <div className="brand-page">
      <Helmet prioritizeSeoTags>
        <link rel="canonical" href={pageUrl} />
        <title>
          État du Lieu – outil de rapport d’adresse (à ne pas confondre avec
          l’état des lieux)
        </title>
        <meta
          name="description"
          content="État du Lieu est un outil en ligne de rapport d’adresse en France, différent de l’état des lieux locatif. Découvrez ce que fait le service et comment le trouver dans Google."
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content="État du Lieu – outil de rapport d’adresse"
        />
        <meta
          property="og:description"
          content="Service de rapport d’adresse pour toute la France métropolitaine : immobilier, climat, risques, services et transports. À ne pas confondre avec l’état des lieux."
        />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:image" content={`${origin}/og/home.png`} />
        <meta property="og:locale" content="fr_FR" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <style>{`
        :root{
          --bg1:#3b82f6; --bg2:#1e40af;
          --surface:rgba(255,255,255,0.95);
          --ink:#0f172a; --muted:#64748b;
          --container-pad:16px;
          --auth-reserve:88px;
        }
        @supports (padding: max(0px)){
          :root{ --auth-reserve: max(76px, calc(env(safe-area-inset-top, 0px) + 64px)); }
        }
        html, body { margin:0; padding:0; color-scheme: light; }
        .brand-page{
          min-height:100vh;
          background: linear-gradient(135deg,var(--bg1) 0%, var(--bg2) 100%);
          color: var(--ink);
          font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Oxygen,Ubuntu,Cantarell,sans-serif;
          position: relative;
        }
        .wrap{
          max-width:900px;
          margin:0 auto;
          padding: calc(28px + var(--auth-reserve)) var(--container-pad) 64px;
        }
        .glass-chip{
          background: var(--surface);
          backdrop-filter: blur(18px);
          border: 1px solid rgba(255,255,255,0.22);
          border-radius: 14px;
          padding: 8px 12px;
          box-shadow: 0 12px 28px rgba(0,0,0,0.12);
          display: inline-flex;
          align-items:center;
          gap:10px;
          color: var(--ink);
          text-decoration: none;
          font-weight: 600;
        }
        .back{ margin: 6px 0 16px; }

        .card{
          background: var(--surface);
          backdrop-filter: blur(20px);
          border-radius: 20px;
          padding: 28px 24px 30px;
          border: 1px solid rgba(255,255,255,0.22);
          box-shadow: 0 20px 40px rgba(0,0,0,0.08);
        }
        .section-label{
          font-size:.8rem;
          font-weight:600;
          letter-spacing:.08em;
          text-transform:uppercase;
          color:#9ca3af;
          margin-bottom:4px;
        }
        h1{
          font-size: clamp(2rem, 4vw, 2.6rem);
          margin: 0 0 8px;
          letter-spacing:-.01em;
        }
        h2{
          font-size:1.25rem;
          margin:22px 0 8px;
        }
        p{
          color:var(--muted);
          line-height:1.7;
          margin: .4rem 0 .9rem;
        }
        ul{
          color:var(--muted);
          padding-left:1.3rem;
          margin:.25rem 0 .9rem;
          line-height:1.7;
        }
        li{ margin:.15rem 0; }
        strong{ color:#111827; font-weight:600; }
        a{ color:#2563eb; text-decoration:none; }
        a:hover{ text-decoration:underline; }

        .subtitle{
          font-size:.96rem;
          color:var(--muted);
          margin:0 0 12px;
        }
        .brand-chip-row{
          display:flex;
          flex-wrap:wrap;
          gap:8px;
          margin:6px 0 12px;
        }
        .brand-chip{
          display:inline-flex;
          align-items:center;
          gap:8px;
          padding:8px 12px;
          border-radius:999px;
          background:rgba(15,23,42,0.07);
          font-size:.9rem;
          font-weight:600;
          color:var(--ink);
        }
        .brand-chip strong{
          font-weight:700;
        }
        .brand-links{
          display:flex;
          flex-wrap:wrap;
          gap:8px;
          margin:8px 0 0;
        }
        .brand-link-btn{
          display:inline-flex;
          align-items:center;
          justify-content:center;
          padding:8px 12px;
          border-radius:10px;
          border:1px solid #e5e7eb;
          background:#f9fafb;
          font-size:.95rem;
          font-weight:600;
          color:var(--ink);
          text-decoration:none;
        }
        .brand-link-btn span{
          margin-right:6px;
        }

        @media (max-width: 640px){
          :root, html, body, .brand-page{ color-scheme: light; background:#ffffff; }
          .card{ background:#fff !important; }
        }
      `}</style>

      <div className="wrap">
        <HeaderAuth floating top={12} right={12} />
        <div className="back">
          <Link className="glass-chip back-chip" to="/">← Retour</Link>
        </div>

        <article className="card" aria-label="À propos d’État du Lieu">
          <header>
            <div className="section-label">À propos</div>
            <h1>État du Lieu – notre outil de rapport d’adresse</h1>
            <p className="subtitle">
              Cette page présente ce qu’est le service État du Lieu, en quoi il est
              différent de l’état des lieux locatif, et comment le retrouver
              facilement dans les moteurs de recherche.
            </p>
            <p>
              Dernière mise à jour : <strong>25 novembre 2025</strong>
            </p>
          </header>

          <section id="definition">
            <h2>Qu’est-ce qu’État du Lieu&nbsp;?</h2>
            <p>
              <strong>État du Lieu</strong> (sans s) est un service web qui permet
              d’obtenir un <strong>rapport complet</strong> sur une adresse en France
              métropolitaine : prix immobiliers observés, loyers, météo actuelle et
              climat futur, risques environnementaux, services, transports, profils
              socio-démographiques, et d’autres indicateurs utiles.
            </p>
            <p>
              Concrètement, vous renseignez une adresse, et le site génère un
              rapport clair, prêt à être consulté en ligne ou exporté en PDF, pour
              gagner du temps dans vos recherches et mieux comprendre l’environnement
              d’un bien.
            </p>
            <p>Pour nous retrouver plus facilement dans Google, vous pouvez taper :</p>
            <p className="brand-chip-row">
              <span className="brand-chip">🔍 <strong>etatdulieu</strong></span>
              <span className="brand-chip">🔍 <strong>état du lieu</strong></span>
            </p>
            <div className="brand-links">
              <Link className="brand-link-btn" to="/">
                <span>🏠</span>Revenir à la page d’accueil
              </Link>
              <Link className="brand-link-btn" to="/villes/">
                <span>🌍</span>Voir un exemple par ville
              </Link>
            </div>
          </section>

          <section id="difference">
            <h2>État du Lieu et état des lieux : deux notions différentes</h2>
            <p>
              En français, le terme <strong>état des lieux</strong> désigne
              habituellement le document établi à l’entrée ou à la sortie d’un
              locataire (bail d’habitation, logement, etc.). C’est un acte
              juridique, encadré par la loi, qui décrit l’état du logement.
            </p>
            <p>
              <strong>État du Lieu</strong>, notre service, est différent :
            </p>
            <ul>
              <li>
                nous ne réalisons pas d’<strong>états des lieux</strong> au sens
                juridique ou contractuel ;
              </li>
              <li>
                nous produisons un <strong>rapport d’adresse</strong> basé sur de
                nombreuses bases de données publiques et vérifiées ;
              </li>
              <li>
                notre objectif est d’aider les particuliers et les professionnels à
                analyser l’environnement d’un bien (quartier, risques, climat,
                services, transports, etc.).
            </li>
            </ul>
            <p>
              Si vous cherchez un modèle ou un guide d’<em>état des lieux</em> pour un
              bail de location, d’autres sites spécialisés seront plus adaptés. Si
              vous cherchez notre outil de <strong>rapport d’adresse</strong>, vous
              êtes au bon endroit.
            </p>
          </section>

          <section id="recherche">
            <h2>Quand taper « état du lieu » dans Google&nbsp;?</h2>
            <p>
              Vous pouvez taper <strong>etatdulieu</strong> ou{" "}
              <strong>état du lieu</strong> dans Google lorsque vous recherchez :
            </p>
            <ul>
              <li>
                un <strong>rapport détaillé</strong> et visuel sur une adresse
                précise ;
              </li>
              <li>
                des informations sur les <strong>prix immobiliers</strong> et les
                loyers autour d’un bien ;
              </li>
              <li>
                des indicateurs de <strong>météo et de climat futur</strong> à long
                terme (tendance de réchauffement, extrêmes, etc.) ;
              </li>
              <li>
                les <strong>risques environnementaux</strong> (inondations, feux de
                forêt, mouvements de terrain, retrait-gonflement des argiles, etc.) ;
              </li>
              <li>
                la présence de <strong>services, transports, écoles, commerces</strong>{" "}
                et autres points d’intérêt à proximité.
              </li>
            </ul>
            <p>Pour une recherche rapide, vous pouvez par exemple saisir :</p>
            <p className="brand-chip-row">
              <span className="brand-chip">
                🔍 <strong>etatdulieu rapport adresse</strong>
              </span>
            </p>
          </section>

          <section id="fonctionnement">
            <h2>Comment fonctionne le service&nbsp;?</h2>
            <p>
              État du Lieu s’appuie sur des <strong>données publiques françaises</strong>{" "}
              (fichiers de transactions immobilières DVF, jeux de données climat,
              données de risques, d’urbanisme, de transports, de services, etc.) que
              nous collectons, nettoyons et agrégons.
            </p>
            <p>
              Pour chaque adresse, nous calculons des indicateurs et produisons des
              cartes et graphiques, afin que le rapport soit à la fois riche en
              informations et lisible. Le rapport peut ensuite être consulté en
              ligne et exporté en PDF.
            </p>
            <p>
              Les données sont mises à jour régulièrement, selon leur fréquence
              officielle (de l’hebdomadaire au pluriannuel selon les sources).
            </p>
          </section>

          <section id="infos-complementaires">
            <h2>Informations complémentaires</h2>
            <p>
              Pour en savoir plus sur le fonctionnement du site et les aspects
              juridiques, vous pouvez consulter :
            </p>
            <ul>
              <li>
                les{" "}
                <Link to="/conditions">
                  Conditions générales d’utilisation et de vente
                </Link>{" "}
                ;
              </li>
              <li>
                la page <Link to="/rgpd">RGPD & politique de confidentialité</Link>.
              </li>
            </ul>
            <p>
              Pour toute question ou demande, vous pouvez nous écrire à{" "}
              <a href={`mailto:${mail}`}>{mail}</a>.
            </p>
          </section>
        </article>
      </div>
    </div>
  );
}
